/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import $ from 'jquery';
import Q from 'q';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import isMobileApp from 'js/lib/isMobileApp';
import type UserAgentInfo from 'js/lib/useragent';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import type { Theme } from '@coursera/cds-core';
import { breakpoints } from '@coursera/cds-core';

import { isPluginUpdatedUiEnabled } from 'bundles/authoring/featureFlags';
import type ApplicationStoreType from 'bundles/ssr/stores/ApplicationStore';
import {
  connectToWidget as connectToWidgetAction,
  registerWidget as registerWidgetAction,
  toggleWidgetError,
} from 'bundles/widget/actions/WidgetManagerActions';
import LegacyWidgetFrame from 'bundles/widget/components/LegacyWidgetFrame';
import { Loader } from 'bundles/widget/components/Loader';
import WidgetErrorMessage from 'bundles/widget/components/WidgetErrorMessage';
import { WidgetInPlaceModal, inPlaceModalStyles } from 'bundles/widget/components/cds/WidgetInPlaceModal';
import type { WidgetInPlaceModalRef } from 'bundles/widget/components/cds/WidgetInPlaceModal';
import { WidgetExpandButton, WidgetInPlaceModalHeader } from 'bundles/widget/components/cds/WidgetInPlaceModalHeader';
import ItemCompleteButton from 'bundles/widget/components/itemCompleteButton/ItemCompleteButton';
import { RPCActions, WidgetReadyStates } from 'bundles/widget/constants/WidgetConstants';
import { useRemoveWidgetAction } from 'bundles/widget/hooks/useRemoveWidgetAction';
import type { ScormConfig } from 'bundles/widget/lib/scorm/scorm-connect/types/courseraApi';
import type {
  ConnectedWidget,
  WidgetError,
  store as WidgetManagerStoreType,
  WidgetRegistry,
  WidgetState,
} from 'bundles/widget/reducers/WidgetManagerReducer';
import type { CourseraConnectMessage } from 'bundles/widget/types/Request';
import type { WidgetSession, WidgetSessionConfiguration, WidgetSessionId } from 'bundles/widget/types/WidgetSession';
import { isRpcAction } from 'bundles/widget/types/WidgetSession';
import { handleWidgetRpcAction } from 'bundles/widget/utils';
import { isDialogSupported } from 'bundles/widget/utils/isDialogSupported';

import _t from 'i18n!nls/widget';

import 'css!bundles/widget/components/__styles__/WidgetFrame';

const styles = {
  // common styles for both expanded and unexpanded cases
  root: css`
    height: 100%;
    margin-bottom: 10px;

    .widget-container {
      height: 100%;

      .frame-container {
        position: relative;
        overflow: auto;
        width: 100%;
        height: 100%;
        -webkit-overflow-scrolling: touch;
      }
    }
  `,

  hideIframe: css`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    height: 100%;

    .frame-container {
      position: relative;
      overflow: auto;
      width: 100%;
      height: 100%;
      -webkit-overflow-scrolling: touch;
    }
  `,

  widgetIframe: css`
    border: none;
    width: 100%;
    display: block;
    position: relative;
  `,
  hidden: css`
    visibility: hidden;
  `,

  unexpandedWidgetContainer: css`
    width: 100%;
    padding-left: 5px;
    padding-right: 5px;
  `,
  controlsRow: css`
    margin-top: var(--cds-spacing-400);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  `,
  markAsComplete: css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-300);
    }
  `,
  completedButtonOnly: css`
    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,
};

type PropsFromStore = {
  widgetRegistry: WidgetRegistry;
  widgetState?: WidgetState;
  error?: WidgetError;

  userAgent: UserAgentInfo;
};

export type PropsFromCaller = {
  onReceiveMessage?: (
    request: CourseraConnectMessage
  ) => Q.Promise<string | undefined | void> | Promise<string | undefined | void>;
  session: WidgetSession;
  sessionId?: WidgetSessionId;
  inWidgetItem?: boolean;
  isDisabled?: boolean;
  // NOTE: isComplete and markComplete should only be true when inWidgetItem=true
  isComplete?: boolean;
  markComplete?: () => void;
  widgetId: string;
  parentRpcActions?: Array<string>;
  fallbackDefaultHeight?: string;
  showPopupButton?: boolean;
  widgetContentTitle?: string;
  onToggleExpanded?: () => void;
  nextItem?: ItemMetadata;
  modalHeaderCss?: Interpolation<Theme>;
};

export type PropsToComponent = PropsFromStore & PropsFromCaller;

type Stores = {
  WidgetManagerStore: InstanceType<typeof WidgetManagerStoreType>;
  ApplicationStore: ApplicationStoreType;
};

type onReceiveMessageResponse = WidgetSessionConfiguration | string | undefined | void | any;

export const WidgetFrame = (props: PropsToComponent) => {
  const {
    modalHeaderCss,
    onReceiveMessage: onReceiveMessageFromProps = () => {
      return Q();
    },
    inWidgetItem = false,
    parentRpcActions = [],
    fallbackDefaultHeight = '500px',
    showPopupButton = true,
    session,
    sessionId,
    widgetId,
    onToggleExpanded,
    // note isComplete and markComplete should only exist if inWidgetItem true
    markComplete,
    isComplete,
    widgetState,
    widgetRegistry,
    error,
    widgetContentTitle,
    nextItem,
    userAgent,
  } = props;
  const executeAction = useFluxibleAction();
  const { rpcActionTypes, configuration, src, sandbox } = session;

  const [expanded, setExpanded] = useState<boolean>(false);
  const [specifiedHeight, setSpecifiedHeight] = useState<string>('');

  const registeredWidgetRef = useRef<boolean | undefined>();
  const attemptedConnectionRef = useRef<boolean | undefined>();

  const WidgetModalRef = useRef<WidgetInPlaceModalRef | null>(null);

  const iframeElementRef = useRef<HTMLIFrameElement | null>(null);

  const isComponentMounted = useRef<boolean>(false);

  const onExpandToggle = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    // TODO @sgogia
    // slightly hacky way of ensuring body (higher in hierarchy) can't be scrolled when widget expanded
    // document body checks are only required for getting rid of flow issues (body should be defined for this event)

    if (!expanded) {
      $('body').addClass(' noscroll');
    } else {
      $('body').removeClass(' noscroll');
    }

    if (onToggleExpanded) {
      onToggleExpanded();
    }

    const shouldExpand = !expanded;
    setExpanded(shouldExpand);
    if (shouldExpand) {
      WidgetModalRef.current?.showWidgetInModal();
    } else if (!shouldExpand) {
      WidgetModalRef.current?.showWidgetInPlace();
    }
  }, [
    expanded,
    onToggleExpanded,
    //  showWidgetInPlace, showWidgetInModal
  ]);

  const onReceiveMessage = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (request: CourseraConnectMessage): Q.Promise<onReceiveMessageResponse> | Promise<onReceiveMessageResponse> => {
      if (request.type === RPCActions.LOAD_WIDGET_ERROR) {
        const { body } = request;
        if (body) {
          const { error: errorFromBody } = body;
          executeAction(toggleWidgetError, { widgetId, error: errorFromBody });
        }
        return Q();
      }
      if (rpcActionTypes?.length) {
        if (isRpcAction(request.type) && rpcActionTypes.includes(request.type)) {
          if (request.type === RPCActions.GET_SESSION_CONFIGURATION) {
            return Q(configuration);
          } else if (request.type === RPCActions.SET_COMPLETE) {
            markComplete?.();
            return onReceiveMessageFromProps?.(request);
          } else if (request.type === RPCActions.SET_HEIGHT) {
            const { body } = request;
            if (body) {
              const specifiedHeightFromBody = body.height;
              if (specifiedHeightFromBody) {
                setSpecifiedHeight(specifiedHeightFromBody);
              }
              return Q('Height set');
            } else {
              return Q('Failed to set height - empty request');
            }
          } else if (parentRpcActions && parentRpcActions.includes(request.type) && onReceiveMessageFromProps) {
            return onReceiveMessageFromProps(request);
          } else if (sessionId) {
            return handleWidgetRpcAction(request, sessionId);
          }
        } else {
          // Should never really happen
          return Q.reject(`Unknown or unauthorized request type: ${request.type}`);
        }
      }

      return Q();
    },
    [
      configuration,
      executeAction,
      markComplete,
      onReceiveMessageFromProps,
      parentRpcActions,
      rpcActionTypes,
      sessionId,
      widgetId,
    ]
  );

  const refreshWidgetState = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    const registerWidget = () => {
      if (registeredWidgetRef.current) {
        return;
      }

      registeredWidgetRef.current = true;
      executeAction(registerWidgetAction, {
        widgetId,
        widgetRegistry,
      });
    };

    const connectToWidget = () => {
      if (attemptedConnectionRef.current) {
        return;
      }

      attemptedConnectionRef.current = true;
      const widgetRegistration = widgetRegistry[widgetId];

      if (widgetRegistration && iframeElementRef.current) {
        // Since DOM elements can't be passed through actionContext, send wrapper around `postMessage`.
        const sendMessageToIFrame = (message: $TSFixMe, srcArg: $TSFixMe) => {
          if (iframeElementRef.current?.contentWindow) {
            iframeElementRef.current.contentWindow?.postMessage(message, srcArg);
          }
        };

        executeAction(connectToWidgetAction, {
          iFrameSrc: iframeElementRef.current.src || '',
          widgetId,
          rpcActionTypes: rpcActionTypes ?? [],
          onReceiveMessage,
          sendMessageToIFrame,
        });
      }
    };

    if (!widgetState || widgetState.readyState === WidgetReadyStates.NOT_LOADED) {
      registerWidget();
    } else if (widgetId && widgetState.readyState === WidgetReadyStates.REGISTERING) {
      connectToWidget();
    }
  }, [executeAction, onReceiveMessage, rpcActionTypes, widgetId, widgetRegistry, widgetState]);

  const showExpansionButton = useMemo(() => {
    const isOnMobile = () => {
      const isMobileBrowser = userAgent && userAgent.isMobileBrowser;
      return isMobileApp.get() || isMobileBrowser;
    };

    return !isOnMobile() && showPopupButton;
  }, [showPopupButton, userAgent]);

  const showCompleteButton = useMemo(() => {
    // If this is a SCORM plugin, we do not know ahead of time if the SCO will mark itself as complete so, although we
    // have the SET_COMPLETE RPC action, it might never be called. In this case, we let the configuration option set by the author
    // determine whether or not to show the completion button
    const isScorm = !!(session?.configuration as ScormConfig)?.SCORM_entry;
    const hideCompleteButtonForScorm = isScorm && (session?.configuration as ScormConfig)?.hideCompleteButton;
    const hasCompletedRPCAction =
      !isScorm && session?.rpcActionTypes && session.rpcActionTypes.includes(RPCActions.SET_COMPLETE);
    const showCompletionButton = !expanded && inWidgetItem && !hasCompletedRPCAction && !hideCompleteButtonForScorm;
    return showCompletionButton;
  }, [expanded, inWidgetItem, session?.configuration, session.rpcActionTypes]);

  const trySmartHeightDefault = () => {
    const iframe = iframeElementRef.current;
    if (!!iframe && !specifiedHeight) {
      try {
        const contentsHeight = iframe?.contentWindow?.document.body.scrollHeight;
        if (contentsHeight && contentsHeight > 0) {
          iframe.height = contentsHeight + 'px';
        }
      } catch (e) {
        // if we can't get the height of the contents, we'll keep our default height
      }
    }
  };

  useEffect(() => {
    refreshWidgetState();
    // previously on all componentDidUpdate
  }, [props, refreshWidgetState, expanded, specifiedHeight]);

  useEffect(() => {
    if (!isComponentMounted.current) {
      if (iframeElementRef?.current instanceof HTMLIFrameElement) {
        refreshWidgetState();
      }

      // showWidgetInPlace();
      isComponentMounted.current = true;
    }
  }, [
    refreshWidgetState,
    // showWidgetInPlace
  ]);

  const widgetRegistration = useMemo(() => widgetRegistry[widgetId], [widgetId, widgetRegistry]);

  const removeListener = useMemo(() => {
    if (widgetRegistration?.isConnected) {
      return (widgetRegistration as ConnectedWidget)?.removeListenerHandle;
    }
    return undefined;
  }, [widgetRegistration]);

  const widgetReadyState = useMemo(() => widgetState?.readyState, [widgetState]);
  useRemoveWidgetAction(widgetId, removeListener, widgetReadyState, executeAction);

  const height = expanded ? undefined : specifiedHeight || fallbackDefaultHeight;
  const width = '100%';

  if (!widgetState) {
    return <Loader width={width} height={height} />;
  }

  const { readyState } = widgetState;
  const isWidgetLoading = readyState === WidgetReadyStates.REGISTERING || readyState === WidgetReadyStates.NOT_LOADED;
  const shouldShowCompleteButton = showCompleteButton && typeof isComplete === 'boolean' && !!markComplete;

  return (
    <React.Fragment>
      <WidgetInPlaceModal
        expanded={expanded}
        modalStyles={{ modal: styles.root, container: expanded ? undefined : styles.unexpandedWidgetContainer }}
        header={
          <WidgetInPlaceModalHeader
            widgetContentTitle={widgetContentTitle}
            onExpandToggle={onExpandToggle}
            css={modalHeaderCss}
          />
        }
        ref={WidgetModalRef}
      >
        {error && <WidgetErrorMessage />}
        {/* this was left as is but essentially it looks like the iframe dom node needs to exist for the onLoad handler so we cant not render the iframe during loading. probably worth reconsidering in future. */}
        {isWidgetLoading && <Loader width={width} height={height} css={styles.hideIframe} />}
        <iframe
          title={session.iframeTitle}
          src={src}
          id={widgetId}
          height={height}
          width={width}
          sandbox={sandbox}
          ref={iframeElementRef}
          onLoad={trySmartHeightDefault}
          data-testid="widget-iframe"
          allow="camera"
          // Because reference to iFrame is needed, hide iFrame via CSS instead of not rendering it.
          css={[styles.widgetIframe, !!error && styles.hidden, expanded && inPlaceModalStyles.expandedWidgetIframe]}
        >
          {_t('Iframes are not supported in your browser.')}
        </iframe>
      </WidgetInPlaceModal>
      {showExpansionButton && (
        <div className="widget-controls" css={styles.controlsRow}>
          <WidgetExpandButton
            onClick={onExpandToggle}
            label={_t('Expand #{widgetContentTitle} window', { widgetContentTitle })}
          />
          {shouldShowCompleteButton && (
            <div css={isComplete ? {} : styles.markAsComplete}>
              <ItemCompleteButton isComplete={isComplete} markComplete={markComplete} nextItem={nextItem} />
            </div>
          )}
        </div>
      )}

      {!showExpansionButton && shouldShowCompleteButton && (
        <div
          className="widget-controls"
          aria-hidden={expanded}
          css={[styles.controlsRow, isComplete ? styles.completedButtonOnly : {}]}
        >
          <ItemCompleteButton isComplete={isComplete} markComplete={markComplete} />
        </div>
      )}
    </React.Fragment>
  );
};

const WidgetFrameWithState = connectToStores<PropsToComponent, PropsFromCaller, Stores>(
  ['WidgetManagerStore', 'ApplicationStore'],
  ({ WidgetManagerStore, ApplicationStore }, props) => {
    const widgetManagerStoreState = WidgetManagerStore.getState();
    const { widgetId } = props;
    const widgetState = widgetManagerStoreState.widgetStates[widgetId];
    const error = widgetState?.error;

    return {
      widgetRegistry: widgetManagerStoreState.widgetRegistry,
      widgetState,
      error,
      userAgent: ApplicationStore.getUserAgent(),
    };
  }
)(WidgetFrame);

const GatedWidgetFrame = (props: PropsFromCaller) => {
  if (!isDialogSupported()) {
    return <LegacyWidgetFrame {...props} />;
  }
  return isPluginUpdatedUiEnabled() ? <WidgetFrameWithState {...props} /> : <LegacyWidgetFrame {...props} />;
};

export default GatedWidgetFrame;
